const PATTERNS_TO_REMOVE = [
  /^a=extmap.*\r\n/gm,
  /^a=msid-semantic: WMS.*\r\n/gm,
  /^a=group:BUNDLE.*\r\n/gm,
  /^a=rtpmap:13.*\r\n/gm,
  /^a=rtpmap:63.*\r\n/gm,
  /^a=fmtp:63.*\r\n/gm,
  /^a=rtpmap:111.*\r\n/gm,
  /^a=fmtp:111.*\r\n/gm,
  /^a=rtcp-fb:111.*\r\n/gm,
];

const TELEPHONE_EVENT_REPLACEMENTS = [
  { pattern: /a=rtpmap:110 telephone-event\/48000/g, replacement: 'a=rtpmap:101 telephone-event/48000' },
  { pattern: /a=rtpmap:126 telephone-event\/8000/g, replacement: 'a=rtpmap:101 telephone-event/8000' },
];

const EXCLUDED_CODECS = ['13', '63', '110', '111', '126'];
const CODEC_PRIORITY_ORDER = ['9', '8', '0'];

const removePattern = (sdp: string, pattern: RegExp): string => sdp.replace(pattern, '');
const replaceTelephoneEvent = (sdp: string, pattern: RegExp, replacement: string): string => sdp.replace(pattern, replacement);

const processAudioMediaLine = (sdp: string): string => {
  return sdp.replace(/m=audio (\d+) (UDP\/TLS\/RTP\/SAVPF|RTP\/AVP).*/, (match) => {
    const [audio, port, protocol, ...allCodecs] = match.split(' ');
    const filteredCodecs = allCodecs.filter((codec) => !EXCLUDED_CODECS.includes(codec));
    const needsDtmf = (allCodecs.includes('110') || allCodecs.includes('126')) && !filteredCodecs.includes('101');

    const codecGroups = {
      prioritized: [] as string[],
      other: [] as string[],
    };

    for (const priorityCodec of CODEC_PRIORITY_ORDER) {
      if (filteredCodecs.includes(priorityCodec)) {
        codecGroups.prioritized.push(priorityCodec);
      }
    }

    codecGroups.other = filteredCodecs.filter((codec) => !CODEC_PRIORITY_ORDER.includes(codec));

    if (needsDtmf) codecGroups.other.push('101');

    const orderedCodecs = [...codecGroups.prioritized, ...codecGroups.other];
    return `${audio} ${port} ${protocol} ${orderedCodecs.join(' ')}`;
  });
};

/**
 * Modifies the media description line in SDP to reorder and filter audio codecs.
 *
 * Example of an SDP media line:
 * "m=audio 9 UDP/TLS/RTP/SAVPF 13 63 111 9 0 8 126"
 *
 * Breaking down the components:
 * - "m=audio": Media type declaration for audio stream
 * - "9": Port number (9 is commonly used for WebRTC when ICE is used)
 * - "UDP/TLS/RTP/SAVPF" or "RTP/AVP": Protocol stack identifier
 *   - UDP/TLS/RTP/SAVPF: Used in WebRTC for secure RTP with feedback
 *   - RTP/AVP: Audio Video Profile, simpler protocol for standard RTP
 * - "13 63 111 9 0 8 126": List of payload type numbers representing supported codecs
 *   - 13: CN (comfort noise) - excluded, not supported
 *   - 63: RED (redundant audio encoding, RFC 2198) - excluded, not supported
 *   - 111: Opus (high-quality audio codec) - excluded, not supported
 *   - 9: G.722 (wideband audio codec)
 *   - 0: PCMU (G.711 μ-law, standard telephony codec)
 *   - 8: PCMA (G.711 A-law, standard telephony codec)
 *   - 126: telephone-event for DTMF tones
 *
 * This function:
 * 1. Removes excluded codec IDs (13, 63, 110, 111, 126)
 * 2. Adds DTMF support (101) if needed
 * 3. Reorders codecs by priority: G.722, PCMA, PCMU, then others
 * 4. Reconstructs the media line with the modified codec order
 */
export const sdpModifierHandler = (description: RTCSessionDescriptionInit) => {
  if (!description.sdp) return Promise.resolve(description);

  let modifiedSdp = description.sdp;
  for (const pattern of PATTERNS_TO_REMOVE) {
    modifiedSdp = removePattern(modifiedSdp, pattern);
  }

  for (const { pattern, replacement } of TELEPHONE_EVENT_REPLACEMENTS) {
    modifiedSdp = replaceTelephoneEvent(modifiedSdp, pattern, replacement);
  }

  modifiedSdp = processAudioMediaLine(modifiedSdp);

  description.sdp = modifiedSdp;
  return Promise.resolve(description);
};
